import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Megaphone,
  Pause,
  Play,
  RotateCw,
  X,
  Calendar,
  Building2,
  MapPin,
  Clock,
  ChevronRight,
  Sparkles,
  Info,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Priority visual configuration
 */
const PRIORITY_CONFIG = {
  urgent: {
    label: 'URGENT',
    badge: 'bg-rose-500/20 text-rose-300 border-rose-500/40',
    dot: 'bg-rose-500 animate-ping',
    banner: 'border-l-rose-500',
  },
  high: {
    label: 'HIGH',
    badge: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
    dot: 'bg-amber-400',
    banner: 'border-l-amber-500',
  },
  medium: {
    label: 'NOTICE',
    badge: 'bg-blue-500/20 text-blue-300 border-blue-500/40',
    dot: 'bg-blue-400',
    banner: 'border-l-blue-500',
  },
  low: {
    label: 'INFO',
    badge: 'bg-slate-500/20 text-slate-300 border-slate-500/40',
    dot: 'bg-slate-400',
    banner: 'border-l-slate-400',
  },
};

export const CompanyNewsTicker = ({ initialAnnouncements = [], hideWhenEmpty = true }) => {
  const [announcements, setAnnouncements] = useState(initialAnnouncements || []);
  const [isPaused, setIsPaused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const scrollContainerRef = useRef(null);

  // Filter out any locally expired announcements based on current client time
  const pruneExpired = useCallback((list) => {
    const now = new Date();
    return (list || []).filter((item) => {
      if (item.never_expires) return true;
      if (!item.expires_at) return true;
      const exp = new Date(item.expires_at);
      return exp > now;
    });
  }, []);

  // Fetch ticker items from backend API
  const fetchTickerAnnouncements = useCallback(async (showIndicator = false) => {
    if (showIndicator) setIsRefreshing(true);
    try {
      const res = await axios.get(`${API_URL}/announcements/ticker`, { withCredentials: true });
      if (res.data && res.data.success) {
        const active = pruneExpired(res.data.announcements || []);
        setAnnouncements(active);
      }
    } catch (err) {
      console.warn('Failed to refresh announcements ticker:', err.message);
    } finally {
      if (showIndicator) {
        setTimeout(() => setIsRefreshing(false), 500);
      }
    }
  }, [pruneExpired]);

  // Sync initial announcements
  useEffect(() => {
    if (initialAnnouncements && initialAnnouncements.length > 0) {
      setAnnouncements(pruneExpired(initialAnnouncements));
    } else {
      fetchTickerAnnouncements(false);
    }
  }, [initialAnnouncements, pruneExpired, fetchTickerAnnouncements]);

  // Periodic polling every 45 seconds to fetch latest broadcasts
  useEffect(() => {
    const pollInterval = setInterval(() => {
      fetchTickerAnnouncements(false);
    }, 45000);
    return () => clearInterval(pollInterval);
  }, [fetchTickerAnnouncements]);

  // High-frequency client-side timer (every 5 seconds) to immediately prune newly expired items
  useEffect(() => {
    const expiryInterval = setInterval(() => {
      setAnnouncements((prev) => {
        const pruned = pruneExpired(prev);
        if (pruned.length !== prev.length) {
          return pruned;
        }
        return prev;
      });
    }, 5000);
    return () => clearInterval(expiryInterval);
  }, [pruneExpired]);

  // Keydown listener for Esc key to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setSelectedAnnouncement(null);
      }
    };
    if (selectedAnnouncement) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedAnnouncement]);

  // If no announcements and hideWhenEmpty is set, don't render anything
  if (!announcements || announcements.length === 0) {
    if (hideWhenEmpty) return null;
    return (
      <div data-testid="company-news-ticker-empty" className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs text-slate-500 flex items-center gap-2">
        <Info className="w-4 h-4 text-slate-400 shrink-0" />
        <span>No active company broadcasts at this time.</span>
      </div>
    );
  }

  const hasUrgent = announcements.some((a) => (a.priority || '').toLowerCase() === 'urgent');

  // Duplicate list to achieve continuous seamless loop
  const displayItems = announcements.length > 1 ? [...announcements, ...announcements] : announcements;

  return (
    <section
      role="region"
      aria-label="Company Broadcasts and News Ticker"
      data-testid="company-news-ticker"
      className="relative rounded-2xl bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white shadow-md border border-indigo-900/40 overflow-hidden"
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between">
        {/* Left Badge: Header Label & Counter */}
        <div className="flex items-center gap-2.5 px-4 py-2.5 sm:py-3 bg-indigo-600/30 sm:bg-transparent backdrop-blur-md border-b sm:border-b-0 sm:border-r border-indigo-500/20 shrink-0 z-10">
          <div className="relative flex items-center justify-center w-8 h-8 rounded-xl bg-indigo-500/20 border border-indigo-400/30 text-indigo-300">
            <Megaphone className="w-4 h-4" />
            {hasUrgent && (
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-rose-500 ring-2 ring-slate-900 animate-pulse" />
            )}
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-bold uppercase tracking-wider text-indigo-200">Company News</span>
              <span className="px-1.5 py-0.2 rounded-full text-[10px] font-bold bg-indigo-500/40 text-indigo-200 border border-indigo-400/30">
                {announcements.length}
              </span>
            </div>
          </div>
        </div>

        {/* Middle: Horizontally Moving Ticker Track */}
        <div
          ref={scrollContainerRef}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className="relative flex-1 overflow-hidden py-2.5 px-2 select-none group"
        >
          {/* Subtle gradient fades on left & right edge for sleek transition */}
          <div className="absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-slate-900 to-transparent pointer-events-none z-10 hidden sm:block"></div>
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-slate-900 to-transparent pointer-events-none z-10 hidden sm:block"></div>

          <div
            className={`animate-ticker-marquee flex items-center gap-6 ${isPaused || isHovered ? 'ticker-paused' : ''}`}
            style={{ animationPlayState: isPaused || isHovered ? 'paused' : 'running' }}
          >
            {displayItems.map((item, idx) => {
              const pConfig = PRIORITY_CONFIG[(item.priority || 'medium').toLowerCase()] || PRIORITY_CONFIG.medium;
              const dateStr = item.date || (item.start_at ? item.start_at.substring(0, 10) : '');

              return (
                <button
                  key={`${item.id}-${idx}`}
                  type="button"
                  data-testid={`ticker-item-${item.id}`}
                  onClick={() => setSelectedAnnouncement(item)}
                  className="flex items-center gap-2.5 text-left text-xs bg-white/5 hover:bg-white/10 active:bg-white/15 px-3 py-1.5 rounded-xl border border-white/10 hover:border-indigo-400/40 transition-all duration-150 shrink-0 max-w-md md:max-w-xl group/chip cursor-pointer focus:outline-hidden focus:ring-2 focus:ring-indigo-400"
                >
                  {/* Priority Badge */}
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold border ${pConfig.badge}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${pConfig.dot}`} />
                    {pConfig.label}
                  </span>

                  {/* Title */}
                  <span className="font-semibold text-slate-100 group-hover/chip:text-indigo-200 transition-colors truncate">
                    {item.title}
                  </span>

                  {/* Content Snippet */}
                  <span className="text-slate-400 truncate max-w-[200px] sm:max-w-[260px] hidden md:inline">
                    — {item.content || item.description || ''}
                  </span>

                  {/* Branch Tag if targeted */}
                  {item.branch_name && item.branch_name !== 'All Branches' && (
                    <span className="hidden lg:inline-flex items-center gap-1 text-[10px] text-indigo-300/80 bg-indigo-500/10 px-1.5 py-0.5 rounded border border-indigo-500/20">
                      <MapPin className="w-2.5 h-2.5" />
                      {item.branch_name}
                    </span>
                  )}

                  {/* Date */}
                  {dateStr && (
                    <span className="text-[10px] text-slate-500 shrink-0 hidden sm:inline">
                      {dateStr}
                    </span>
                  )}

                  <ChevronRight className="w-3.5 h-3.5 text-slate-500 group-hover/chip:text-indigo-300 group-hover/chip:translate-x-0.5 transition-all shrink-0" />
                </button>
              );
            })}
          </div>
        </div>

        {/* Right: Controls (Pause/Resume, Refresh) */}
        <div className="flex items-center justify-end gap-1.5 px-3 py-1.5 sm:py-2 border-t sm:border-t-0 sm:border-l border-indigo-500/20 shrink-0 bg-slate-900/60 sm:bg-transparent">
          {/* Pause / Resume Button */}
          <button
            type="button"
            onClick={() => setIsPaused(!isPaused)}
            data-testid="ticker-pause-toggle-btn"
            aria-label={isPaused ? 'Resume announcement ticker' : 'Pause announcement ticker'}
            title={isPaused ? 'Resume moving ticker' : 'Pause ticker'}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 active:scale-95 transition-all focus:outline-hidden"
          >
            {isPaused ? <Play className="w-3.5 h-3.5 text-emerald-400" /> : <Pause className="w-3.5 h-3.5" />}
          </button>

          {/* Refresh Button */}
          <button
            type="button"
            onClick={() => fetchTickerAnnouncements(true)}
            data-testid="ticker-refresh-btn"
            aria-label="Refresh announcements"
            title="Refresh broadcasts"
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 active:scale-95 transition-all focus:outline-hidden"
          >
            <RotateCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin text-indigo-400' : ''}`} />
          </button>
        </div>
      </div>

      {/* FULL ANNOUNCEMENT MODAL DIALOG */}
      {selectedAnnouncement && (
        <div
          data-testid="announcement-detail-modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200"
          onClick={() => setSelectedAnnouncement(null)}
        >
          <div
            className="bg-white text-slate-900 rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-slate-200 animate-in zoom-in-95 duration-150"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="announcement-modal-title"
          >
            {/* Modal Header */}
            <div className="relative bg-gradient-to-r from-slate-900 to-indigo-950 p-6 text-white">
              <button
                type="button"
                onClick={() => setSelectedAnnouncement(null)}
                data-testid="close-announcement-modal-btn"
                className="absolute top-4 right-4 p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="flex items-center gap-2 mb-2">
                {(() => {
                  const pConfig = PRIORITY_CONFIG[(selectedAnnouncement.priority || 'medium').toLowerCase()] || PRIORITY_CONFIG.medium;
                  return (
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold border ${pConfig.badge}`}>
                      <span className={`w-2 h-2 rounded-full ${pConfig.dot}`} />
                      {pConfig.label} PRIORITY
                    </span>
                  );
                })()}
                {selectedAnnouncement.company_name && (
                  <span className="text-xs text-indigo-200 font-medium flex items-center gap-1">
                    <Building2 className="w-3.5 h-3.5" />
                    {selectedAnnouncement.company_name}
                  </span>
                )}
              </div>

              <h2 id="announcement-modal-title" className="text-lg md:text-xl font-bold tracking-tight text-white">
                {selectedAnnouncement.title}
              </h2>

              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-indigo-200/80">
                {selectedAnnouncement.start_at && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    Published: {selectedAnnouncement.start_at.substring(0, 10)}
                  </span>
                )}
                {selectedAnnouncement.branch_name && selectedAnnouncement.branch_name !== 'All Branches' && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" />
                    Target: {selectedAnnouncement.branch_name}
                  </span>
                )}
                {selectedAnnouncement.expires_at ? (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    Expires: {selectedAnnouncement.expires_at.substring(0, 10)}
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-emerald-300">
                    <Sparkles className="w-3.5 h-3.5" />
                    Indefinite Display Policy
                  </span>
                )}
              </div>
            </div>

            {/* Modal Body / Full Message */}
            <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="prose prose-sm max-w-none text-slate-700 leading-relaxed whitespace-pre-line text-sm">
                {selectedAnnouncement.content || selectedAnnouncement.description || 'No detailed message provided.'}
              </div>

              {selectedAnnouncement.created_by_name && (
                <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                  <span>Posted by: <strong>{selectedAnnouncement.created_by_name}</strong></span>
                  {selectedAnnouncement.priority === 'urgent' && (
                    <span className="text-rose-600 font-semibold flex items-center gap-1">
                      Action / Attention Required
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-slate-50 px-6 py-3 border-t border-slate-100 flex items-center justify-end">
              <button
                type="button"
                onClick={() => setSelectedAnnouncement(null)}
                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-xl text-xs font-semibold transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default CompanyNewsTicker;
